// Package imports:
import React, { useEffect, useState } from "react";
// Component imports:
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import Table from "../../ui-elements/Table/Table";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import Loading from "../../ui-elements/Loading/Loading";
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import Button from "../../ui-elements/Button/Button";
import ForeignPepModal from "./ForeignPepModal";
import Link from "../../ui-elements/Link/Link";
// Service imports:
import { GET_KELDAN_API_URL } from "../../services/config";
import { getTableDate } from "../../services/utils";
import { ErrorMessages } from "../../services/errorMessages";
// Type imports:
import { Fetched } from "../../types/Types";
import { IKeldanApiResponse } from "../../types/KeldanTypes";
import { ForeignPep, InitRequest, PepResult } from "../../types/PepTypes";
import { formatCategories } from "./PepPage";
import { CountryList } from "../../assets/staticData/CountryList";

interface IfetchedData {
  eventDate: string;
  reply: string;
}

const ViewForeignReport: React.FC<{ id: string }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<PepResult | undefined>(undefined);
  const [fetchedData, setFetchedData] = useState<Fetched<ForeignPep>>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `${GET_KELDAN_API_URL()}/PEP/GetForeignReport/${id}`;
        const response = await fetch(url);
        if (response.ok) {
          const responseBody: IKeldanApiResponse<IfetchedData> = await response.json();
          if (responseBody.result) {
            //parse the data and create the data object
            const parsedData: ForeignPep = JSON.parse(responseBody.result.reply);
            setFetchedData(parsedData);
          } else if (responseBody.message) {
            setFetchedData(new Error(responseBody.message));
          } else {
            setFetchedData(new Error(ErrorMessages.OtherShort));
          }
        } else {
          setFetchedData(new Error(ErrorMessages.RequestFailed));
        }
      } catch (e) {
        setFetchedData(new Error(ErrorMessages.NetworkError));
      }
    };
    fetchData();
  }, []);

  const displayResult = (data: ForeignPep) => {
    // null indicates loading
    if (data === undefined) return <></>;
    if (data === null) return <Loading />;

    // Check if connected
    return (
      <>
        <div style={{ marginTop: "10px" }}>
          <Table
            showEmptyTableText
            tableSize="lg"
            data={data.match ?? []}
            columns={[
              {
                title: "Match Rate",
                renderCell: ({ match_rate }) => (match_rate ? match_rate : "-"),
                textAlign: "left",
              },
              {
                title: "Nafn",
                renderCell: ({ fullName }) => fullName,
                textAlign: "left",
              },
              {
                title: "Fæðingardagur",
                renderCell: ({ dateOfBirth }) => dateOfBirth,
                textAlign: "left",
              },
              {
                title: "Þjóðerni",
                renderCell: ({ nationalitiesIsoCodes }) => nationalitiesIsoCodes?.map(code => CountryList.find(country => country.value === code)?.label).join("/"),
                textAlign: "left",
              },
              {
                title: "Flokkur/ar",
                renderCell: ({ categories }) => formatCategories(categories),
                textAlign: "left",
              },
              {
                title: "Ítarefni",
                renderCell: (item) => (
                  <Button
                    onClick={() => {
                      setModalData(item);
                      setShowModal(true);
                    }}
                    buttonType="secondary"
                    size="sm"
                  >
                    Skoða
                  </Button>
                ),
              },
            ]}
          />
        </div>
      </>
    );
  };

  function displayInitRequest(request: InitRequest | undefined) {
    if (request === undefined) return <></>;
    return (
      <>
        <div className="info-col" key={'fullName'}>
          <div className="info-item">
            <span className="title">Nafn</span>
            <span className="value" style={{ textTransform: "capitalize" }}>
              {request.firstName} {request.middleName ? request.middleName + " " : ""}{request.lastName}
            </span>
          </div>
        </div>
        <div className="info-col" key={'dob'}>
          <div className="info-item">
            <span className="title">Fæðingardagur</span>
            <span className="value" style={{ textTransform: "capitalize" }}>
              {request.dateOfBirth ?? '-'}
            </span>
          </div>
        </div>
        <div className="info-col" key={'countryCode'}>
          <div className="info-item">
            <span className="title">Landakóði</span>
            <span className="value" style={{ textTransform: "capitalize" }}>
              {request.countryCode ?? '-'}
            </span>
          </div>
        </div>
      </>
    );
  }

  const displayReport = () => {
    if (fetchedData === null) return <Loading />;
    if (fetchedData instanceof Error) return <ErrorAlert error={fetchedData} />;

    return (
      <>
        <div className="info-col-wrapper">{displayInitRequest(fetchedData.request)}</div>
        <DisplayBox>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="italic-text">
              Skýrsla sótt:{" "}
              {fetchedData.date && fetchedData.date.length > 0 && fetchedData.date[0].date
                ? getTableDate(fetchedData.date[0].date, "DD/MM/YYYY HH:MM:SS", ".")
                : "-"}
            </div>
            <Link targetBlank linkSize="15" url={`/Pdf/Althjodlegir-valistar-einstaklingar/${id}`} icon="pdf">
              Sækja skýrslu
            </Link>
          </div>
          <div className="main">{displayResult(fetchedData)}</div>
        </DisplayBox>
      </>
    );
  };

  return (
    <div className="main KCL_viewPep KCL_individual-profile assets-report">
      <div className="shell">
        <HeadSection title="Alþjóðlegir válistar" />
        {displayReport()}
      </div>
      <ForeignPepModal show={showModal} close={() => setShowModal(false)} data={modalData ? [modalData] : undefined} />
    </div>
  );
};

export default ViewForeignReport;
